import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotificationRow } from "@/components/ui/notification-row";
import useMusioApi from "@/contexts/client/musio-client-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { Edit } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const ERROR_CHANGING_PASSWORD =
  "There was an error changing your password. Please check your inputs and try again later";
const SUCCESS_CHANGING_PASSWORD = "Success!";

interface FormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const PasswordModal = () => {
  const { musioApi } = useMusioApi();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      if (formError) setFormError(null);
    },
    [formError]
  );

  const { mutate, isError, isSuccess, isPending, error } =
    musioApi.useMutation("post", "/user/password");

  const isDisabled = useMemo(
    () => form.currentPassword.trim().length === 0 || isPending,
    [form.currentPassword, isPending]
  );

  useEffect(() => {
    if (isSuccess) {
      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [isSuccess]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (form.newPassword !== form.confirmPassword) {
        setFormError("New passwords don't match");
        return;
      }

      mutate({
        body: {
          password: form.currentPassword,
          newPassword: form.newPassword,
        },
      });
    },
    [form, mutate]
  );

  const hasNotification = isSuccess || formError != null || isError;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="p-2 px-5 bg-musio-mid-gray text-musio-light-lilac hover:text-musio-mid-gray">
          {isMobile ? <Edit /> : "Change Password"}
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="AlertDialogOverlay" />
        <DialogContent className="AlertDialogContent">
          <form onSubmit={handleSubmit} className="min-h-0 flex flex-col gap-4">
            <DialogTitle className="flex items-center justify-between gap-4 p-8 bg-musio-bg">
              <div className="text-[30px] font-semibold text-musio-light-lilac">
                Change Password
              </div>

              <DialogClose asChild>
                <Button
                  type="button"
                  disabled={isSuccess}
                  className="select-none px-8 h-[38px] rounded-full font-bold bg-musio-red text-musio-light-lilac"
                >
                  <span className="text-sm">Go Back</span>
                </Button>
              </DialogClose>
            </DialogTitle>

            <div className="p-8 bg-musio-bg flex flex-col gap-2">
              {hasNotification && (
                <NotificationRow
                  intent={formError || isError ? "error" : "success"}
                >
                  {isSuccess
                    ? SUCCESS_CHANGING_PASSWORD
                    : formError
                      ? formError
                      : error?.error?.passwordConstraint ??
                      ERROR_CHANGING_PASSWORD}
                </NotificationRow>
              )}

              <div className="flex flex-col gap-1">
                <p className="text-musio-light-lilac text-lg">
                  Current password
                </p>
                <Input
                  type="password"
                  name="currentPassword"
                  value={form.currentPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="current-password"
                />
              </div>

              <div className="flex flex-col gap-1">
                <p className="text-musio-light-lilac text-lg">New password</p>
                <Input
                  type="password"
                  name="newPassword"
                  value={form.newPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="flex flex-col gap-1">
                <p className="text-musio-light-lilac text-lg">
                  Confirm password
                </p>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  disabled={isSuccess}
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            <DialogFooter className="p-8 flex flex-col justify-center items-center gap-2 bg-musio-bg">
              {!isSuccess ? (
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className="w-[404px] h-[35px] rounded-2xl font-bold bg-musio-green disabled:opacity-60"
                >
                  <p className="text-sm text-musio-mid-gray">
                    {isPending ? "Submitting…" : "Submit"}
                  </p>
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    location.reload();
                  }}
                  className="w-[404px] h-[35px] rounded-2xl font-bold bg-musio-green"
                >
                  <p className="text-sm text-musio-mid-gray">Close</p>
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
